import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, CheckCircle, Clock, RefreshCw, AlertTriangle, TrendingUp, Send, X, Search, Filter } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { sendCommissionReleasedEmail } from '../../../services/escrowEmailService';

const formatAmount = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ROLE_CONFIG = {
  agent: { label: 'Agent immobilier', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '🏠' },
  notary: { label: 'Notaire', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '⚖️' },
  platform: { label: 'Plateforme', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '🏢' },
};

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  released: { label: 'Libérée', color: 'bg-green-50 text-green-700 border-green-200' },
  cancelled: { label: 'Annulée', color: 'bg-gray-50 text-gray-500 border-gray-200' },
};

// ─── Commission Calculator Modal ──────────────────────────────────────────────
const CommissionCalculatorModal = ({ transaction, onClose, onSave, loading }) => {
  const totalAmount = transaction?.amount_total || transaction?.total_amount || 0;
  const [agentRate, setAgentRate] = useState(3);
  const [notaryRate, setNotaryRate] = useState(2);
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [notaryName, setNotaryName] = useState('');
  const [notaryEmail, setNotaryEmail] = useState('');

  const agentAmount = Math.round((totalAmount * agentRate) / 100);
  const notaryAmount = Math.round((totalAmount * notaryRate) / 100);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Calculer les commissions</h3>
            <p className="text-sm text-gray-500">Réf: {transaction?.reference || transaction?.id?.slice(0, 8)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Transaction summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-600 font-semibold uppercase tracking-wide mb-1">Montant total de la transaction</p>
            <p className="text-2xl font-bold text-amber-700">{formatAmount(totalAmount, transaction?.currency)}</p>
            {transaction?.title && <p className="text-sm text-amber-600 mt-1">{transaction?.title}</p>}
          </div>

          {/* Agent commission */}
          <div className="border border-blue-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">🏠</span>
              <h4 className="font-semibold text-gray-800">Commission Agent immobilier</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nom de l'agent</label>
                <input type="text" value={agentName} onChange={e => setAgentName(e?.target?.value)} placeholder="Nom complet" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email de l'agent</label>
                <input type="email" value={agentEmail} onChange={e => setAgentEmail(e?.target?.value)} placeholder="agent@email.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Taux (%)</label>
                <input type="number" min="0" max="20" step="0.5" value={agentRate} onChange={e => setAgentRate(parseFloat(e?.target?.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Montant calculé</label>
                <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm font-bold text-blue-700">{formatAmount(agentAmount, transaction?.currency)}</div>
              </div>
            </div>
          </div>

          {/* Notary commission */}
          <div className="border border-purple-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚖️</span>
              <h4 className="font-semibold text-gray-800">Commission Notaire</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nom du notaire</label>
                <input type="text" value={notaryName} onChange={e => setNotaryName(e?.target?.value)} placeholder="Nom complet" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email du notaire</label>
                <input type="email" value={notaryEmail} onChange={e => setNotaryEmail(e?.target?.value)} placeholder="notaire@email.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Taux (%)</label>
                <input type="number" min="0" max="20" step="0.5" value={notaryRate} onChange={e => setNotaryRate(parseFloat(e?.target?.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Montant calculé</label>
                <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm font-bold text-purple-700">{formatAmount(notaryAmount, transaction?.currency)}</div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Commission agent</span><span className="font-semibold text-blue-700">{formatAmount(agentAmount, transaction?.currency)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Commission notaire</span><span className="font-semibold text-purple-700">{formatAmount(notaryAmount, transaction?.currency)}</span></div>
            <div className="flex justify-between border-t border-gray-200 pt-2"><span className="font-semibold text-gray-700">Total commissions</span><span className="font-bold text-gray-900">{formatAmount(agentAmount + notaryAmount, transaction?.currency)}</span></div>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Annuler</button>
            <button
              onClick={() => onSave({ agentRate, agentAmount, agentName, agentEmail, notaryRate, notaryAmount, notaryName, notaryEmail })}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Créer les commissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Finance Commission Dashboard ───────────────────────────────────────
const FinanceCommissionDashboard = () => {
  const [commissions, setCommissions] = useState([]);
  const [releasedTransactions, setReleasedTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [calcModal, setCalcModal] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedTx, setExpandedTx] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [commRes, txRes] = await Promise.all([
        supabase?.from('commission_payouts')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('escrow_transactions')?.select('*')?.in('status', ['RELEASED', 'COMPLETED'])?.order('released_at', { ascending: false })?.limit(50),
      ]);
      setCommissions(commRes?.data || []);
      setReleasedTransactions(txRes?.data || []);
    } catch (err) {
      console.error('Error fetching commission data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateCommissions = async (tx, { agentRate, agentAmount, agentName, agentEmail, notaryRate, notaryAmount, notaryName, notaryEmail }) => {
    setActionLoading(true);
    try {
      const { data: { user: adminUser } } = await supabase?.auth?.getUser();
      const inserts = [];
      if (agentAmount > 0) {
        inserts?.push({
          escrow_transaction_id: tx?.id,
          recipient_role: 'agent',
          recipient_name: agentName || null,
          recipient_email: agentEmail || null,
          commission_rate: agentRate,
          base_amount: tx?.amount_total || tx?.total_amount || 0,
          commission_amount: agentAmount,
          currency: tx?.currency || 'XOF',
          status: 'pending',
        });
      }
      if (notaryAmount > 0) {
        inserts?.push({
          escrow_transaction_id: tx?.id,
          recipient_role: 'notary',
          recipient_name: notaryName || null,
          recipient_email: notaryEmail || null,
          commission_rate: notaryRate,
          base_amount: tx?.amount_total || tx?.total_amount || 0,
          commission_amount: notaryAmount,
          currency: tx?.currency || 'XOF',
          status: 'pending',
        });
      }
      if (inserts?.length > 0) {
        const { error } = await supabase?.from('commission_payouts')?.insert(inserts);
        if (error) throw error;
        // Log action
        await supabase?.from('escrow_action_logs')?.insert({
          escrow_transaction_id: tx?.id,
          admin_user_id: adminUser?.id,
          admin_email: adminUser?.email,
          action: 'commission_payout',
          previous_status: tx?.status,
          new_status: tx?.status,
          amount_affected: agentAmount + notaryAmount,
          currency: tx?.currency || 'XOF',
          notes: `Commissions créées: agent ${agentRate}% (${formatAmount(agentAmount, tx?.currency)}), notaire ${notaryRate}% (${formatAmount(notaryAmount, tx?.currency)})`,
        });
      }
      setCalcModal(null);
      showToast('Commissions créées avec succès');
      await fetchData();
    } catch (err) {
      showToast('Erreur: ' + (err?.message || 'Une erreur est survenue'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReleaseCommission = async (commission) => {
    setActionLoading(true);
    try {
      const { data: { user: adminUser } } = await supabase?.auth?.getUser();
      const { error } = await supabase?.from('commission_payouts')?.update({
        status: 'released',
        released_at: new Date()?.toISOString(),
        released_by: adminUser?.id,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', commission?.id);
      if (error) throw error;

      // Log action
      await supabase?.from('escrow_action_logs')?.insert({
        escrow_transaction_id: commission?.escrow_transaction_id,
        admin_user_id: adminUser?.id,
        admin_email: adminUser?.email,
        action: 'commission_release',
        previous_status: 'pending',
        new_status: 'released',
        amount_affected: commission?.commission_amount,
        currency: commission?.currency,
        notes: `Commission ${commission?.recipient_role} libérée pour ${commission?.recipient_name || 'destinataire inconnu'}`,
      });

      // Send email notification
      if (commission?.recipient_email) {
        const tx = releasedTransactions?.find(t => t?.id === commission?.escrow_transaction_id);
        await sendCommissionReleasedEmail({
          recipientEmail: commission?.recipient_email,
          recipientName: commission?.recipient_name,
          transactionRef: tx?.reference || tx?.id?.slice(0, 8),
          propertyTitle: tx?.title || tx?.item_title,
          commissionAmount: commission?.commission_amount,
          commissionRate: commission?.commission_rate,
          currency: commission?.currency,
          role: commission?.recipient_role,
        });
      }

      showToast('Commission libérée et notification envoyée');
      await fetchData();
    } catch (err) {
      showToast('Erreur: ' + (err?.message || 'Une erreur est survenue'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Stats
  const totalPending = commissions?.filter(c => c?.status === 'pending')?.reduce((s, c) => s + (c?.commission_amount || 0), 0);
  const totalReleased = commissions?.filter(c => c?.status === 'released')?.reduce((s, c) => s + (c?.commission_amount || 0), 0);
  const pendingCount = commissions?.filter(c => c?.status === 'pending')?.length;
  const releasedCount = commissions?.filter(c => c?.status === 'released')?.length;

  // Filter
  const filteredCommissions = commissions?.filter(c => {
    const matchStatus = statusFilter === 'ALL' || c?.status === statusFilter;
    const q = search?.toLowerCase();
    const matchSearch = !q || c?.recipient_name?.toLowerCase()?.includes(q) || c?.recipient_email?.toLowerCase()?.includes(q) || c?.recipient_role?.toLowerCase()?.includes(q);
    return matchStatus && matchSearch;
  });

  // Group released transactions without commissions
  const txWithoutCommissions = releasedTransactions?.filter(tx =>
    !commissions?.some(c => c?.escrow_transaction_id === tx?.id)
  );

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-white text-sm font-medium transition-all ${toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Commissions Agents & Notaires</h3>
          <p className="text-sm text-gray-500 mt-0.5">Calcul et libération automatique des commissions à la libération des fonds séquestre</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Commissions en attente', value: formatAmount(totalPending), sub: `${pendingCount} commission${pendingCount !== 1 ? 's' : ''}`, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Commissions libérées', value: formatAmount(totalReleased), sub: `${releasedCount} libérée${releasedCount !== 1 ? 's' : ''}`, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Transactions libérées', value: releasedTransactions?.length, sub: 'Fonds séquestre libérés', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Sans commission', value: txWithoutCommissions?.length, sub: 'À traiter', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
        ]?.map(s => (
          <div key={s?.label} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl ${s?.bg} flex items-center justify-center flex-shrink-0`}>
              <s.icon className={`w-6 h-6 ${s?.color}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{s?.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s?.label}</p>
              <p className="text-xs text-gray-400">{s?.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Transactions without commissions */}
      {txWithoutCommissions?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h4 className="font-semibold text-amber-800">Transactions libérées sans commissions ({txWithoutCommissions?.length})</h4>
          </div>
          <div className="space-y-2">
            {txWithoutCommissions?.slice(0, 5)?.map(tx => (
              <div key={tx?.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{tx?.title || tx?.item_title || 'Transaction'}</p>
                  <p className="text-xs text-gray-500">Réf: {tx?.reference || tx?.id?.slice(0, 8)} · {formatDate(tx?.released_at || tx?.updated_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-amber-700">{formatAmount(tx?.amount_total || tx?.total_amount, tx?.currency)}</span>
                  <button
                    onClick={() => setCalcModal(tx)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition-colors"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    Calculer
                  </button>
                </div>
              </div>
            ))}
            {txWithoutCommissions?.length > 5 && (
              <p className="text-xs text-amber-600 text-center pt-1">+ {txWithoutCommissions?.length - 5} autres transactions</p>
            )}
          </div>
        </div>
      )}

      {/* Commission list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h4 className="font-bold text-gray-900">Toutes les commissions ({filteredCommissions?.length})</h4>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Rechercher..." className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e?.target?.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
              <option value="ALL">Tous statuts</option>
              <option value="pending">En attente</option>
              <option value="released">Libérées</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center"><RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" /><p className="text-gray-500 text-sm">Chargement...</p></div>
        ) : filteredCommissions?.length === 0 ? (
          <div className="p-12 text-center"><DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 font-medium">Aucune commission trouvée</p><p className="text-gray-400 text-sm mt-1">Les commissions apparaîtront ici après la libération des fonds séquestre</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Destinataire</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rôle</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Taux</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Commission</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCommissions?.map(c => {
                  const roleCfg = ROLE_CONFIG?.[c?.recipient_role] || ROLE_CONFIG?.agent;
                  const statusCfg = STATUS_CONFIG?.[c?.status] || STATUS_CONFIG?.pending;
                  return (
                    <tr key={c?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">{c?.recipient_name || '—'}</p>
                        {c?.recipient_email && <p className="text-xs text-gray-400 mt-0.5">{c?.recipient_email}</p>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${roleCfg?.color}`}>
                          {roleCfg?.icon} {roleCfg?.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right font-medium text-gray-700">{c?.commission_rate}%</td>
                      <td className="px-5 py-4 text-right font-bold text-amber-700">{formatAmount(c?.commission_amount, c?.currency)}</td>
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusCfg?.color}`}>{statusCfg?.label}</span>
                      </td>
                      <td className="px-5 py-4 text-gray-500 text-xs whitespace-nowrap">{formatDate(c?.created_at)}</td>
                      <td className="px-5 py-4 text-center">
                        {c?.status === 'pending' && (
                          <button
                            onClick={() => handleReleaseCommission(c)}
                            disabled={actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 mx-auto"
                          >
                            <Send className="w-3.5 h-3.5" />
                            Libérer
                          </button>
                        )}
                        {c?.status === 'released' && (
                          <span className="text-xs text-green-600 font-medium flex items-center gap-1 justify-center">
                            <CheckCircle className="w-3.5 h-3.5" />
                            {formatDate(c?.released_at)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Commission Calculator Modal */}
      {calcModal && (
        <CommissionCalculatorModal
          transaction={calcModal}
          onClose={() => setCalcModal(null)}
          onSave={(vals) => handleCreateCommissions(calcModal, vals)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default FinanceCommissionDashboard;
